import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { subscriptionAPI, creditsAPI } from '../../services/api';
import type { SubscriptionPlan, UsageStats, CreditPackageOption } from './subscriptionTypes';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { useAuth } from '../../context/AuthContext';
import { invalidatePlansEntitlementsCache } from '../../utils/entitlements';

export function useSubscriptionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const hasHandledPaymentSuccess = useRef(false);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [creditPackages, setCreditPackages] = useState<CreditPackageOption[]>([]);
  const [creditPackagesLoading, setCreditPackagesLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionPlansLoading, setSubscriptionPlansLoading] = useState(true);
  const [currency, setCurrency] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);

  // Empty currency means "not yet resolved from backend" — render guards in the page
  // show a loading skeleton for price rows until this populates. Do NOT emit a default
  // symbol here: flashing `$` before `₹` lands is exactly the bug this story fixes.
  const getCurrencySymbol = (code: string): string => {
    if (!code) return '';
    const symbols: Record<string, string> = {
      usd: '$', aud: 'A$', eur: '€', gbp: '£', jpy: '¥', cad: 'C$', nzd: 'NZ$', inr: '₹',
    };
    return symbols[code.toLowerCase()] ?? code.toUpperCase();
  };

  const fetchSubscriptionPlans = async () => {
    try {
      setSubscriptionPlansLoading(true);
      const response = await subscriptionAPI.getSubscriptionLinks();
      const plans = response.plans || {};
      if (response.currency) setCurrency(response.currency);
      const plansArray = Object.entries(plans).map(([planId, plan]: [string, any]) => ({
        id: planId,
        name: plan.name || planId,
        price: plan.price || 0,
        credits: plan.call_limit || plan.credits || 0,
        features: plan.features || [],
        popular: plan.popular || false
      }));
      setSubscriptionPlans(plansArray);
    } catch (err: any) {
      // No USD fallback — rendering fake $ prices on IN was the LT-195 bug.
      setSubscriptionPlans([]);
      setError('Unable to load subscription plans. Please refresh to try again.');
    } finally {
      setSubscriptionPlansLoading(false);
    }
  };

  const fetchCreditPackages = async () => {
    try {
      setCreditPackagesLoading(true);
      const response = await subscriptionAPI.getCreditLinks();
      const packages = response.packages || {};
      if (response.currency) setCurrency(response.currency);
      const packagesArray = Object.entries(packages).map(([packageId, pkg]: [string, any]) => ({
        credits: pkg.credits || 0,
        price: pkg.price || 0,
        popular: pkg.popular || false,
        packageId: packageId
      }));
      setCreditPackages(packagesArray);
    } catch (err: any) {
      // No USD fallback — same reason as fetchSubscriptionPlans.
      setCreditPackages([]);
      setError('Unable to load credit packages. Please refresh to try again.');
    } finally {
      setCreditPackagesLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      setLoading(true);
      setStatsLoading(true);
      const creditStats = await creditsAPI.getBalance();
      let currentPlan = 'free';
      if (creditStats.subscription_plan && (creditStats.subscription_status === 'active' || creditStats.subscription_status === 'past_due')) {
        currentPlan = creditStats.subscription_plan;
      }
      setUsageStats({
        available_credits: creditStats.available_credits,
        total_purchased: creditStats.total_purchased,
        total_consumed: creditStats.total_consumed,
        subscription_status: creditStats.subscription_status ?? (creditStats.available_credits > 0 ? 'active' : 'inactive'),
        current_plan: currentPlan,
        pending_downgrade_plan: creditStats.pending_downgrade_plan || null,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load credit statistics');
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    // Check if user already has an active subscription
    if (usageStats?.current_plan && usageStats.current_plan !== 'free') {
      // Show confirmation modal
      setPendingPlanId(planId);
      setShowConfirmModal(true);
      return;
    }
    
    // Proceed with subscription
    await proceedWithSubscription(planId);
  };

  const proceedWithSubscription = async (planId: string) => {
    try {
      setSelectedPlan(planId);
      setProcessing(true);
      setError('');
      setShowConfirmModal(false);
      const hasActiveSubscription = usageStats?.current_plan && usageStats.current_plan !== 'free' && usageStats.subscription_status === 'active';
      if (hasActiveSubscription) {
        const response = await subscriptionAPI.changePlan(planId);
        setSuccessMessage(response.message || `Successfully changed to ${planId} plan`);
        track(AnalyticsEvents.SUBSCRIPTION_PLAN_CHANGE, { plan_id: planId });
        setTimeout(() => setSuccessMessage(''), 8000);
        await refreshUser();
        await fetchUsageStats();
        window.scrollTo(0, 0);
      } else {
        const response = await subscriptionAPI.createSubscriptionCheckout(planId);
        const checkoutUrl = response.checkout_url;
        if (!checkoutUrl) {
          setError(`Failed to create checkout session for ${planId} plan`);
          return;
        }
        const plan = subscriptionPlans.find((p) => p.id === planId);
        track(AnalyticsEvents.SUBSCRIPTION_CHECKOUT_STARTED, {
          plan_id: planId,
          value: plan?.price ?? 0,
          currency: currency || undefined,
        });
        window.location.href = checkoutUrl;
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((d: any) => d?.msg ?? d).join(', ') : 'Failed to change plan');
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const cancelSubscriptionChange = () => {
    setShowConfirmModal(false);
    setPendingPlanId(null);
  };

  const handleCancelPendingDowngrade = async () => {
    try {
      setProcessing(true);
      setError('');
      const response = await subscriptionAPI.cancelPendingDowngrade();
      setSuccessMessage(response.message || 'Pending downgrade canceled.');
      track(AnalyticsEvents.PENDING_DOWNGRADE_CANCEL);
      setTimeout(() => setSuccessMessage(''), 6000);
      await fetchUsageStats();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to cancel pending downgrade');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddCredits = async (packageId: string) => {
    try {
      setProcessing(true);
      setError('');
      const response = await subscriptionAPI.createCreditCheckout(packageId);
      const checkoutUrl = response.checkout_url;
      if (!checkoutUrl) {
        setError(`Failed to create checkout session for package ${packageId}`);
        return;
      }
      const pkg = creditPackages.find((p) => p.packageId === packageId);
      track(AnalyticsEvents.CREDIT_CHECKOUT_STARTED, {
        package_id: packageId,
        value: pkg?.price ?? 0,
        currency: currency || undefined,
      });
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setProcessing(true);
      setError('');
      const response = await subscriptionAPI.createCustomerPortal();
      const portalUrl = response.url;
      if (!portalUrl) {
        setError('Failed to create customer portal session');
        return;
      }
      track(AnalyticsEvents.CUSTOMER_PORTAL_OPENED);
      window.location.href = portalUrl;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to open customer portal');
    } finally {
      setProcessing(false);
    }
  };

  // Handle payment success params once. Clear URL immediately to prevent effect re-runs
  // (subscriptionPlans/creditPackages load async and would otherwise trigger multiple refreshUser calls).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentSuccess = params.get('payment_success');
    const planId = params.get('plan_id');
    const packageId = params.get('package_id');
    if (paymentSuccess !== 'true' || hasHandledPaymentSuccess.current) return;
    hasHandledPaymentSuccess.current = true;

    // Clear URL first so location.search updates and effect won't re-run when deps change
    navigate('/subscription', { replace: true });

    // Refresh auth user so sidebar/route guards (SMS, inbound) reflect new subscription immediately
    refreshUser()
      .then(() => {
        invalidatePlansEntitlementsCache();
        window.dispatchEvent(new CustomEvent('entitlements:updated'));
        return fetchUsageStats();
      })
      .catch(() => {});

    let value = 0;
    if (planId) {
      const plan = subscriptionPlans.find((p) => p.id === planId);
      value = plan?.price ?? 0;
    } else if (packageId) {
      const pkg = creditPackages.find((p) => p.packageId === packageId);
      value = pkg?.price ?? 0;
    }
    track(AnalyticsEvents.PAYMENT_SUCCESS, {
      plan_id: planId || undefined,
      package_id: packageId || undefined,
      value,
      currency: currency || undefined,
    });
    if (planId) setSuccessMessage(`Successfully subscribed to ${planId} plan!`);
    else if (packageId) setSuccessMessage(`Successfully purchased ${packageId} credit package!`);
    else setSuccessMessage('Payment successful!');
    setTimeout(() => setSuccessMessage(''), 5000);
  }, [location.search, navigate, refreshUser, subscriptionPlans, creditPackages, currency]);

  // Fetch data once on mount (and when returning to the page) so we don't run twice after replaceState.
  // Currency must land before plans/packages render so the first paint shows the correct symbol
  // (₹ on IN, A$ on AU) instead of flashing $ — the core LT-195 fix.
  useEffect(() => {
    const loadAll = async () => {
      try {
        const res = await subscriptionAPI.getCurrency();
        if (res?.currency) setCurrency(res.currency.toLowerCase());
      } catch (_) {
        // Currency endpoint failure leaves currency empty; render guards show a skeleton.
      }
      fetchUsageStats();
      fetchCreditPackages();
      fetchSubscriptionPlans();
    };
    loadAll();
  }, []);

  return {
    usageStats,
    loading,
    statsLoading,
    error,
    selectedPlan,
    processing,
    successMessage,
    expandedPlan,
    setExpandedPlan,
    creditPackages,
    creditPackagesLoading,
    subscriptionPlans,
    subscriptionPlansLoading,
    currency,
    getCurrencySymbol,
    handleSubscribe,
    handleAddCredits,
    handleManageSubscription,
    handleCancelPendingDowngrade,
    showConfirmModal,
    pendingPlanId,
    proceedWithSubscription,
    cancelSubscriptionChange,
  };
}
